/**
 * Implement a class that represents an Anthropic client
 * and provides a set of tools for converting messages to and from the LLM.
 * - sendThread(thread: Thread): Thread - send a message to the LLM
 * - streamThread(thread: Thread): AsyncGenerator<string, Thread> - stream a message to the LLM
 */

// Use require if module not found error
const Anthropic = require('@anthropic-ai/sdk').default;
import { nanoid } from "nanoid";
import { zodToJsonSchema } from "zod-to-json-schema";
import { Message, ToolCall } from "../message";
import { Thread } from "../thread";
import { Tool } from "../tool";
import { traceable } from "../tracing";
import { BaseModelDriver } from "./base";

// Constants
const MAX_PROMPT_CHARACTERS = 100_000;

/**
 * Available Anthropic models
 */
export type AnthropicModel =
  // Claude 3 models
  | "claude-3-opus-20240229"
  | "claude-3-sonnet-20240229"
  | "claude-3-haiku-20240307"
  | "claude-3-5-sonnet-20240620"
  | "claude-3-7-sonnet-20250219" 
  // Claude 2 models
  | "claude-2.0"
  | "claude-2.1"
  // Tagged versions
  | "claude-3-opus-20240229-v1:0"
  | "claude-3-sonnet-20240229-v1:0"
  | "claude-3-haiku-20240307-v1:0";

/**
 * Configuration options for the Anthropic driver
 */
export interface AnthropicConfig {
  /**
   * Anthropic API Key
   */
  apiKey?: string;

  /**
   * Model to use
   * @default "claude-3-5-sonnet-20240620"
   */
  model?: AnthropicModel;

  /**
   * Temperature for response generation
   * @default 0.7
   */
  temperature?: number;

  /**
   * Maximum number of tokens to generate
   */
  maxTokens?: number;

  /**
   * Whether to cache responses
   * @default false
   */
  cache?: boolean;
  
  /**
   * Enable tracing for this driver
   * @default false
   */
  trace?: boolean;
  
  /**
   * Additional metadata for tracing
   */
  traceMetadata?: Record<string, unknown>;

  /**
   * Tools to use
   */
  tools?: Tool[];
  
  /**
   * Tool choice mode
   * "auto": Let the model decide when to use tools
   * "any": Always try to use a tool
   * "none": Never use tools
   * @default "auto"
   */
  toolChoice?: "auto" | "any" | "none";
}

// Define simplified types for Anthropic SDK components we use
interface AnthropicClient {
  messages: {
    create: (params: any) => Promise<any>;
  }
}

interface AnthropicContentBlockSource {
  type: string;
  media_type: string;
  data: string;
}

interface AnthropicContentBlock {
  type: string;
  text?: string;
  source?: AnthropicContentBlockSource;
  tool_use_id?: string;
  content?: string;
}

interface AnthropicMessageParam {
  role: string;
  content: string | AnthropicContentBlock[];
}

/**
 * Represents an Anthropic client and provides tools for converting messages to/from the LLM
 */
export class AnthropicThreadDriver extends BaseModelDriver {
  /**
   * Anthropic API Client
   */
  private client: AnthropicClient;

  /**
   * Anthropic model to use
   */
  private model: AnthropicModel | string;

  /**
   * Temperature for response generation
   */
  private temperature: number;

  /**
   * Maximum number of tokens to generate
   */
  private maxTokens?: number;

  /**
   * Whether to cache responses
   */
  private cache: boolean;
  
  /**
   * Tool choice mode
   */
  private toolChoice?: "auto" | "any" | "none";

  /**
   * Returns a list of available Anthropic models
   */
  static getAvailableModels(): AnthropicModel[] {
    return [
      // Claude 3 models
      "claude-3-opus-20240229",
      "claude-3-sonnet-20240229",
      "claude-3-haiku-20240307",
      "claude-3-5-sonnet-20240620",
      "claude-3-7-sonnet-20250219",
      // Claude 2 models
      "claude-2.0",
      "claude-2.1",
      // Tagged versions
      "claude-3-opus-20240229-v1:0",
      "claude-3-sonnet-20240229-v1:0",
      "claude-3-haiku-20240307-v1:0"
    ];
  }

  /**
   * Create a new Anthropic driver
   * @param config Configuration options
   */
  constructor(config?: AnthropicConfig) {
    super();

    // Get API Key from config or environment variable
    const apiKey = config?.apiKey || process.env.ANTHROPIC_API_KEY;
    if (!apiKey) {
      throw new Error(
        "Anthropic API Key must be provided in config or as ANTHROPIC_API_KEY environment variable"
      );
    }

    // Create a base client
    const baseClient = new Anthropic({
      apiKey,
      // This is needed because we're in a Node.js environment, in a server context
      dangerouslyAllowBrowser: false, 
    });
    
    // Only wrap with tracing if trace is enabled (default to false)
    if (config?.trace ?? false) {
      // Create a proxy around the Anthropic client to intercept API calls
      this.client = new Proxy(baseClient, {
        get: (target, prop, receiver) => {
          const value = Reflect.get(target, prop, receiver);
          
          // Handle nested properties (messages.create)
          if (prop === 'messages' && value && typeof value === 'object') {
            return new Proxy(value, {
              get: (messagesTarget, messagesProp, messagesReceiver) => {
                const messagesValue = Reflect.get(messagesTarget, messagesProp, messagesReceiver);
                
                // Wrap the create method with tracing
                if (messagesProp === 'create' && typeof messagesValue === 'function') {
                  return traceable(messagesValue.bind(messagesTarget), {
                    run_type: "llm",
                    name: "anthropic",
                    ...config?.traceMetadata,
                    aggregator: (args: any[], result: any) => {
                      // Extract relevant data for tracing
                      const params = args[0] || {};
                      
                      // For streaming responses, we just log the input
                      if (params.stream) {
                        return {
                          model: params.model,
                          input: {
                            messages: params.messages,
                            temperature: params.temperature,
                            max_tokens: params.max_tokens,
                            tools: params.tools ? params.tools.length : 0,
                          },
                          streaming: true
                        };
                      }
                      
                      // For non-streaming, include response data
                      return {
                        model: params.model,
                        input: {
                          messages: params.messages,
                          temperature: params.temperature,
                          max_tokens: params.max_tokens,
                          tools: params.tools ? params.tools.length : 0,
                        },
                        output: result.content,
                        metrics: result.usage ? {
                          prompt_tokens: result.usage.input_tokens,
                          completion_tokens: result.usage.output_tokens,
                          total_tokens: result.usage.input_tokens + result.usage.output_tokens,
                        } : undefined,
                        finish_reason: result.stop_reason
                      };
                    }
                  });
                }
                
                return messagesValue;
              }
            });
          }
          
          return value;
        }
      });
    } else {
      this.client = baseClient;
    }
    
    // Set configuration options
    this.model = config?.model || "claude-3-5-sonnet-20240620";
    this.temperature = config?.temperature ?? 0.7;
    // Always set a default maxTokens if not provided
    this.maxTokens = config?.maxTokens ?? 1024;
    this.cache = config?.cache ?? false;
    this.toolChoice = config?.toolChoice;
  }

  /**
   * Send a thread to the LLM and get a response
   * @param thread Thread to send
   * @returns Updated thread with LLM response
   */
  async sendThread(thread: Thread): Promise<Thread> {
    if (thread.all().length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    this.debug("Sending message to Anthropic API", { model: this.model });

    // Format messages for Anthropic API
    const { messages, systemMessage } = this.formatMessagesForAPI(thread);

    // Check for potential token issues
    const totalCharacters = messages.reduce(
      (sum, msg) => sum + (Array.isArray(msg.content) 
        ? msg.content.reduce((s: number, c: AnthropicContentBlock) => s + (typeof c.text === 'string' ? c.text.length : 0), 0)
        : (typeof msg.content === 'string' ? msg.content.length : 0)),
      0
    );

    if (totalCharacters > MAX_PROMPT_CHARACTERS) {
      throw new Error(`The thread content is too long (${totalCharacters} characters). Try a shorter conversation.`);
    }

    try {
      // Create parameters for the API call
      const params: any = {
        model: this.model,
        messages,
        temperature: this.temperature,
        max_tokens: this.maxTokens,
      };

      // Add system message if specified
      if (systemMessage) {
        params.system = systemMessage;
      }

      // Add tools if there are any in the thread
      const tools = thread.getTools();
      if (tools.length > 0) {
        params.tools = this.createToolDefinitions(tools);
        
        // Format tool_choice as an object instead of a string
        if (this.toolChoice === "any") {
          params.tool_choice = { type: "any" };
        } else if (this.toolChoice === "none") {
          params.tool_choice = { type: "none" };
        } else {
          params.tool_choice = { type: "auto" };
        }
      }

      // Make the API call
      const response = await this.client.messages.create(params);

      // Create a new message from the response
      const message = new Message({
        role: "assistant",
        content: Array.isArray(response.content) 
          ? response.content.map((block: any) => block.text).join('') 
          : response.content,
        toolCalls: this.parseToolCalls(response),
        cache: this.cache,
      });

      // Add the message to the thread
      thread.push(message);

      // Log usage metrics if available
      if (response.usage) {
        this.debug("Anthropic API usage metrics", {
          inputTokens: response.usage.input_tokens,
          outputTokens: response.usage.output_tokens,
          totalTokens: response.usage.input_tokens + response.usage.output_tokens,
        });
      }

      return thread;
    } catch (error) {
      this.error("Error sending message to Anthropic API", { error });
      throw error;
    }
  }

  /**
   * Stream a thread to the LLM and get a streaming response
   * @param thread Thread to send
   * @returns AsyncGenerator yielding the stream and updated thread
   */
  async *streamThread(thread: Thread): AsyncGenerator<
    { stream: AsyncGenerator<string, void>; message: Message }, Thread
  > {
    if (thread.all().length === 0) {
      throw new Error("Cannot stream an empty thread");
    }

    this.debug("Streaming message to Anthropic API", { model: this.model });

    // Format messages for Anthropic API
    const { messages, systemMessage } = this.formatMessagesForAPI(thread);

    // Check for potential token issues
    const totalCharacters = messages.reduce(
      (sum, msg) => sum + (Array.isArray(msg.content) 
        ? msg.content.reduce((s: number, c: AnthropicContentBlock) => s + (typeof c.text === 'string' ? c.text.length : 0), 0)
        : (typeof msg.content === 'string' ? msg.content.length : 0)),
      0
    );

    if (totalCharacters > MAX_PROMPT_CHARACTERS) {
      throw new Error(`The thread content is too long (${totalCharacters} characters). Try a shorter conversation.`);
    }

    try {
      // Create parameters for the API call
      const params: any = {
        model: this.model,
        messages,
        temperature: this.temperature,
        stream: true,
        max_tokens: this.maxTokens,
      };

      // Add system message if specified
      if (systemMessage) {
        params.system = systemMessage;
      }

      // Add tools if there are any in the thread
      const tools = thread.getTools();
      if (tools.length > 0) {
        params.tools = this.createToolDefinitions(tools);
        
        // Format tool_choice as an object instead of a string
        if (this.toolChoice === "any") {
          params.tool_choice = { type: "any" };
        } else if (this.toolChoice === "none") {
          params.tool_choice = { type: "none" };
        } else {
          params.tool_choice = { type: "auto" };
        }
      }

      // Create a new message to add to the thread
      const message = new Message({
        role: "assistant",
        content: "",
        cache: this.cache,
      });

      // Make the streaming API call
      const stream = await this.client.messages.create(params);

      // Create a streaming content generator
      const streamContent = this.createStreamGenerator(stream, message);

      // Yield the stream
      yield { stream: streamContent, message };

      // Add the message to the thread
      thread.push(message);

      // Return the updated thread
      return thread;
    } catch (error) {
      this.error("Error streaming message to Anthropic API", { error });
      throw error;
    }
  }

  /**
   * Create a stream generator that handles updates to the message
   */
  private async *createStreamGenerator(
    stream: AsyncIterable<any>,
    message: Message
  ): AsyncGenerator<string, void> {
    let toolCallsStarted = false;
    let currentToolCalls: any[] = [];
    let contentAccumulated = "";

    try {
      for await (const chunk of stream) {
        // Handle content delta events
        if (chunk.type === 'content_block_delta') {
          const delta = chunk.delta?.text || '';
          
          // Update accumulated content
          contentAccumulated += delta;
          
          // Update the message content
          Object.assign(message, new Message({
            role: "assistant",
            content: contentAccumulated,
            toolCalls: message.toolCalls,
            cache: message.cache,
          }));
          
          yield delta;
        }
        
        // Handle tool use events
        if (chunk.type === 'tool_use') {
          if (!toolCallsStarted) {
            toolCallsStarted = true;
            currentToolCalls.push({
              id: chunk.id,
              name: chunk.name,
              input: chunk.input,
            });
          }
        }
      }

      // Process the tool calls after the stream completes
      if (currentToolCalls.length > 0) {
        const parsedToolCalls = currentToolCalls.map(call => ({
          toolUseId: call.id,
          name: call.name,
          arguments: call.input,
        }));
        
        // Create a new message with the tool calls
        const updatedMessage = new Message({
          role: "assistant",
          content: contentAccumulated,
          toolCalls: parsedToolCalls,
          cache: message.cache,
        });
        
        // Copy the updated message properties
        Object.assign(message, updatedMessage);
      }
    } catch (error) {
      this.error("Error processing Anthropic stream", { error });
      throw error;
    }
  }

  /**
   * Format messages from the Thread object to the Anthropic API format
   */
  private formatMessagesForAPI(thread: Thread): { 
    messages: AnthropicMessageParam[]; 
    systemMessage?: string;
  } {
    const formattedMessages: AnthropicMessageParam[] = [];
    let systemMessage: string | undefined;

    // Track tool use IDs to ensure we have tool_use before tool_result
    const toolUseIds = new Set<string>();

    for (const message of thread.all()) {
      // Handle system messages separately
      if (message.role === "system") {
        systemMessage = message.content || "";
        continue;
      }

      // Track tool calls for assistant messages - must come BEFORE processing tool results
      if (message.role === "assistant" && message.toolCalls && message.toolCalls.length > 0) {
        // First, add the assistant message with its content
        const contentBlocks: AnthropicContentBlock[] = [];
        
        if (message.content) {
          contentBlocks.push({
            type: "text",
            text: message.content,
          });
        }
        
        formattedMessages.push({
          role: "assistant",
          content: contentBlocks.length > 0 ? contentBlocks : "I'll use a tool to help with that.",
        });
        
        // Then, add tool use blocks
        for (const toolCall of message.toolCalls) {
          // Add this tool use ID to our tracking set
          toolUseIds.add(toolCall.toolUseId);
          
          // Create a specific tool use block for Anthropic
          formattedMessages.push({
            role: "assistant",
            content: [
              {
                type: "tool_use",
                id: toolCall.toolUseId,
                name: toolCall.name,
                input: toolCall.arguments,
              } as any
            ] as any
          });
        }
        
        continue;
      }

      // Handle tool results - but only if we've seen the corresponding tool use
      if (message.toolResults && message.toolResults.length > 0) {
        // Filter tool results to only include those with known tool use IDs
        const validToolResults = message.toolResults.filter(result => 
          toolUseIds.has(result.toolUseId)
        );
        
        if (validToolResults.length > 0) {
          // Process each tool result individually
          for (const toolResult of validToolResults) {
            formattedMessages.push({
              role: "user",
              content: [
                {
                  type: "tool_result",
                  tool_use_id: toolResult.toolUseId,
                  content: JSON.stringify(toolResult.result || toolResult.error),
                } as any
              ] as any
            });
          }
        }
        
        // Also add user message text if it exists - as a separate message
        if (message.content) {
          formattedMessages.push({
            role: "user",
            content: message.content,
          });
        }
        continue;
      }

      // Process attachments if they exist
      if (message.attachments && message.attachments.length > 0) {
        const contentParts: AnthropicContentBlock[] = [];
        
        // Add text content if it exists
        if (message.content) {
          contentParts.push({
            type: "text",
            text: message.content,
          });
        }
        
        // Add each attachment
        for (const attachment of message.attachments) {
          if (attachment.type === "image" && attachment.source.uri) {
            contentParts.push({
              type: "image",
              source: {
                type: "base64",
                media_type: `image/${attachment.format}`,
                data: attachment.source.uri.replace(/^data:image\/\w+;base64,/, ''),
              }
            });
          }
        }
        
        // Add the message with content parts
        formattedMessages.push({
          role: message.role === "user" ? "user" : "assistant",
          content: contentParts,
        });
        continue;
      }

      // Default case for simple messages
      if (message.role === "user" || message.role === "assistant") {
        formattedMessages.push({
          role: message.role,
          content: message.content || "",
        });
      }
    }

    return { messages: formattedMessages, systemMessage };
  }

  /**
   * Create tool definitions for the Anthropic API
   */
  private createToolDefinitions(tools: Tool[]): Array<any> {
    return tools.map(tool => ({
      name: tool.getName(),
      description: tool.getDescription(),
      input_schema: zodToJsonSchema(tool.getParameters()),
    }));
  }

  /**
   * Parse tool calls from the Anthropic API response
   */
  private parseToolCalls(response: any): ToolCall[] | undefined {
    if (!response.content || !Array.isArray(response.content)) {
      return undefined;
    }

    // Extract tool calls from response content blocks
    const toolCalls: ToolCall[] = [];
    
    for (const block of response.content) {
      if (block.type === 'tool_use') {
        try {
          toolCalls.push({
            toolUseId: block.id || nanoid(),
            name: block.name,
            arguments: block.input || {},
          });
        } catch (error) {
          this.error("Error parsing tool call arguments", {
            error,
            block,
          });
        }
      }
    }
    
    return toolCalls.length > 0 ? toolCalls : undefined;
  }
} 